import {
  Banknote,
  Bitcoin,
  BookOpen,
  Brain,
  BrainCircuit,
  Cpu,
  FlaskConical,
  Gavel,
  Globe,
  GraduationCap,
  Handshake,
  HeartPulse,
  Landmark,
  Leaf,
  type LucideIcon,
  Newspaper,
  Palette,
  Rocket,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Tag,
  TrendingUp,
  Users,
} from 'lucide-react';
import './domain-badges.scss';

/**
 * URG-9 curated domain labels → lucide icon. Keys are the exact strings URG
 * emits (see ur-metrics-generator `gateway/scoring/domains.py` DOMAIN_CATEGORIES).
 * An unmapped label falls back to a generic Tag so future domain additions
 * render safely before this map catches up. These are functional stand-ins —
 * a brand design pass will swap in the custom icon set (follow-up ticket).
 */
const DOMAIN_ICONS: Record<string, LucideIcon> = {
  Politics: Landmark,
  'Government & Law': Gavel,
  Geopolitics: Globe,
  'Foreign Policy': Handshake,
  'Society & Human Rights': Users,
  'Media & Journalism': Newspaper,
  Finance: Banknote,
  'Economy & Business': TrendingUp,
  Crypto: Bitcoin,
  Technology: Cpu,
  'AI & Data': BrainCircuit,
  Cybersecurity: ShieldCheck,
  Science: FlaskConical,
  Space: Rocket,
  'Climate & Environment': Leaf,
  'Health & Medicine': HeartPulse,
  'Mind & Psychology': Brain,
  History: ScrollText,
  'Philosophy & Ethics': BookOpen,
  Education: GraduationCap,
  'Culture & Arts': Palette,
  Futurism: Sparkles,
};

const DEFAULT_MAX_VISIBLE = 4;

interface DomainBadgesProps {
  domains: string[];
  /** Icons shown inline before the rest collapse into a "+N" chip. */
  maxVisible?: number;
}

/**
 * Renders a user's background/expertise domains as a compact row of icon
 * badges (TRIL-289). Domains arrive frequency-sorted from URG, so truncating
 * to `maxVisible` keeps the strongest. Renders nothing when there are no
 * domains (the common case until Phase 6 populates story-quality history).
 */
export const DomainBadges = ({
  domains,
  maxVisible = DEFAULT_MAX_VISIBLE,
}: DomainBadgesProps): React.ReactElement | null => {
  if (!domains || domains.length === 0) return null;

  const visible = domains.slice(0, maxVisible);
  const overflow = domains.slice(maxVisible);

  return (
    <ul className="domain-badges" aria-label="Background and expertise domains">
      {visible.map((domain) => {
        const Icon = DOMAIN_ICONS[domain] ?? Tag;
        return (
          <li
            key={domain}
            className="domain-badges__badge"
            title={domain}
            aria-label={domain}
          >
            <Icon size={16} aria-hidden="true" />
          </li>
        );
      })}
      {overflow.length > 0 && (
        <li
          className="domain-badges__overflow"
          title={overflow.join(', ')}
          aria-label={`${overflow.length} more: ${overflow.join(', ')}`}
        >
          +{overflow.length}
        </li>
      )}
    </ul>
  );
};

export default DomainBadges;
